import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Translate as TranslateIcon,
  Language as LanguageIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { translationService, SUPPORTED_LANGUAGES } from '../services/translationService';

interface TranslateMessageProps {
  messageId: string;
  originalText: string;
  compact?: boolean;
  onTranslationComplete?: (translatedText: string) => void;
}

export const TranslateMessage: React.FC<TranslateMessageProps> = ({
  messageId,
  originalText,
  compact = false,
  onTranslationComplete
}) => {
  const { settings } = useUserSettings();
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const [isSameLanguage, setIsSameLanguage] = useState(false);
  const [shouldShowTranslateButton, setShouldShowTranslateButton] = useState(true);
  const [isDetectingLanguage, setIsDetectingLanguage] = useState(false);

  // Check if translation button should be shown by detecting language
  useEffect(() => {
    const checkIfTranslationNeeded = async () => {
      if (!settings.translation.enabled) {
        setShouldShowTranslateButton(false);
        return;
      }

      if (!originalText || originalText.trim().length === 0) {
        setShouldShowTranslateButton(false);
        return;
      }

      const targetLang = settings.translation.targetLanguage === 'auto' 
        ? translationService.getUserLanguage() 
        : settings.translation.targetLanguage;

      // First do basic checks to avoid unnecessary API calls
      if (!translationService.needsTranslation(originalText, targetLang)) {
        setShouldShowTranslateButton(false);
        return;
      }

      try {
        setIsDetectingLanguage(true);
        const detected = await translationService.detectLanguage(originalText);
        setDetectedLanguage(detected);

        // Compare detected language with target language
        if (detected === targetLang) {
          setShouldShowTranslateButton(false);
          setIsSameLanguage(true);
        } else {
          setShouldShowTranslateButton(true);
          setIsSameLanguage(false);
        }
      } catch (error) {
        console.error('Language detection failed:', error);
        // If detection fails, show the button to allow manual translation
        setShouldShowTranslateButton(true);
      } finally {
        setIsDetectingLanguage(false);
      }
    };

    checkIfTranslationNeeded();
  }, [originalText, settings.translation]);

  const handleTranslate = async () => {
    if (isTranslating || translatedText) return;

    try {
      setIsTranslating(true);
      setError(null);
      setIsSameLanguage(false);

      const targetLang = settings.translation.targetLanguage === 'auto' 
        ? translationService.getUserLanguage() 
        : settings.translation.targetLanguage;

      const result = await translationService.translateText(originalText, targetLang);
      
      // Check if translation was actually needed
      if (result.translatedText === originalText && result.detectedSourceLanguage === targetLang) {
        // No translation needed - same language, hide the translation component entirely
        setTranslatedText(null);
        setIsSameLanguage(true);
        // Don't call onTranslationComplete since no translation occurred
        return;
      }
      
      setTranslatedText(result.translatedText);
      setDetectedLanguage(result.detectedSourceLanguage || null);
      setIsSameLanguage(false);
      
      onTranslationComplete?.(result.translatedText);
    } catch (err) {
      console.error('Translation failed:', err);
      setError(err instanceof Error ? err.message : 'Translation failed');
      setIsSameLanguage(false);
    } finally {
      setIsTranslating(false);
    }
  };

  // Auto-translate if enabled and conditions are met
  useEffect(() => {
    const shouldAutoTranslate = () => {
      if (!settings.translation.enabled || !settings.translation.autoTranslate) return false;
      if (!shouldShowTranslateButton) return false; // Don't auto-translate if button shouldn't show
      
      return true;
    };

    if (shouldAutoTranslate()) {
      handleTranslate();
    }
  }, [shouldShowTranslateButton, settings.translation.autoTranslate, handleTranslate]);

  const getLanguageDisplayName = (code: string) => {
    return SUPPORTED_LANGUAGES[code as keyof typeof SUPPORTED_LANGUAGES] || code;
  };

  // Don't show anything if translation is disabled or if same language detected
  if (!settings.translation.enabled || (!shouldShowTranslateButton && !translatedText && !isDetectingLanguage)) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* Translation Controls */}
      {!translatedText && !isTranslating && shouldShowTranslateButton && (
        <Tooltip 
          title="Translate this message to your preferred language"
          placement="top"
        >
          <Button
            size="small"
            startIcon={<TranslateIcon />}
            onClick={handleTranslate}
            variant="outlined"
            sx={{ 
              fontSize: '0.75rem', 
              minHeight: 28,
              opacity: 0.8,
              '&:hover': { opacity: 1 }
            }}
          >
            Translate
          </Button>
        </Tooltip>
      )}

      {/* Language Detection Loading */}
      {isDetectingLanguage && !translatedText && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Checking language...
          </Typography>
        </Box>
      )}

      {isTranslating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Translating...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 1, py: 0.5 }}
          icon={<ErrorIcon fontSize="small" />}
        >
          <Typography variant="caption">
            Translation failed: {error}
          </Typography>
        </Alert>
      )}

      {/* Translated Content */}
      {translatedText && (
        <Box sx={{ mt: 1 }}>
          {/* Translation Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 0.5,
            flexWrap: 'wrap'
          }}>
            <Chip
              icon={<LanguageIcon />}
              label="Translated"
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            
            {detectedLanguage && (
              <Chip
                label={`from ${getLanguageDisplayName(detectedLanguage)}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}

          </Box>

          {/* Translated Text */}
          <Box sx={{ 
            p: 1, 
            bgcolor: 'primary.light', 
            color: 'primary.contrastText',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main'
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {isSameLanguage ? (
                <>{translatedText}</>
              ) : (
                <><strong>Translation:</strong> {translatedText}</>
              )}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}; 